import { useEffect, useMemo, useRef, useState } from "react";
import { askDwellMinutes } from "../../../shared/dwellAi";
import { coordsMovedMeters } from "../../../shared/geo";
import { SOCIAL_OPTIONS } from "../../../shared/moods";
import {
  buildCircularRoute,
  buildLeisureRoute,
  fetchNearbyPlacesCascade,
  googleMapsLoop,
  leafletMapHtml,
  type TravelMode,
} from "../../../shared/places";
import {
  cascadeRadiiMeters,
  placesForPlan,
  PLACE_KIND_LABELS,
  type SettingPref,
} from "../../../shared/plans";
import type { NearbyPlace, SocialContext } from "../../../shared/types";
import { useLiveLocation } from "../lib/useLiveLocation";
import { useCategories } from "../lib/useCategories";
import { CategoryFilter } from "../components/CategoryFilter";
import { SemiRoutePanel } from "../components/SemiRoutePanel";
import { filterPlacesByCategories } from "../../../shared/categories";

const TIME_OPTIONS = [30, 45, 60, 90, 120, 180];
const MODES: { key: TravelMode; label: string }[] = [
  { key: "walk", label: "A pie" },
  { key: "run", label: "Corriendo" },
  { key: "bike", label: "En bici" },
  { key: "car", label: "En coche" },
];

type TimeMode = "trayecto" | "libre";
type RouteKind = "aleatoria" | "semi";

export default function Routes() {
  const { coords, error: gpsError, ready: gpsReady } = useLiveLocation();
  const [minutes, setMinutes] = useState(60);
  const [mode, setMode] = useState<TravelMode>("walk");
  const [timeMode, setTimeMode] = useState<TimeMode>("trayecto");
  const [stayLocal, setStayLocal] = useState(false);
  const [social, setSocial] = useState<SocialContext>("solo");
  const [setting, setSetting] = useState<SettingPref>("any");
  const [routeKind, setRouteKind] = useState<RouteKind>("aleatoria");
  const [categories, setCategories] = useCategories();
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [stops, setStops] = useState<NearbyPlace[]>([]);
  const [status, setStatus] = useState("Activando GPS en vivo…");
  const [busy, setBusy] = useState(false);
  const lastFetch = useRef<{
    latitude: number;
    longitude: number;
    mode: TravelMode;
    minutes: number;
    stayLocal: boolean;
  } | null>(null);
  const planGen = useRef(0);

  useEffect(() => {
    if (!coords) {
      if (gpsReady) setStatus(gpsError || "Permite la ubicación en el candado del navegador.");
      return;
    }
    const how =
      coords.source === "ip"
        ? "ubicación aproximada"
        : `GPS en vivo ±${Math.round(coords.accuracyMeters || 0)} m`;
    const prev = lastFetch.current;
    const moved = prev ? coordsMovedMeters(prev, coords) : 9999;
    if (prev && prev.mode === mode && prev.minutes === minutes && prev.stayLocal === stayLocal && moved < 120) {
      return;
    }
    lastFetch.current = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      mode,
      minutes,
      stayLocal,
    };
    let cancelled = false;
    setStatus(`Estás aquí (${how}). Buscando sitios cercanos…`);
    void (async () => {
      try {
        const radii = cascadeRadiiMeters({ mode, minutes, stayLocal });
        const fetched = await fetchNearbyPlacesCascade({ ...coords, radii });
        if (cancelled) return;
        setPlaces(fetched.places);
        setStatus(
          fetched.places.length
            ? `${fetched.places.length} sitios cerca (${how}, ~${(fetched.radiusUsed / 1000).toFixed(1)} km). Pulsa Buscar ruta.`
            : `Ubicación ok (${how}). Poca oferta a este radio; prueba coche o más tiempo.`,
        );
      } catch {
        if (!cancelled) setStatus(`Ubicación ok (${how}), pero no pude leer sitios.`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coords, mode, minutes, stayLocal, gpsReady, gpsError]);

  // El filtro de categorías manda sobre «con quién»: primero se filtra por categoría y luego por compañía y entorno.
  const visiblePlaces = useMemo(
    () => placesForPlan(filterPlacesByCategories(places, categories), { social, setting }),
    [places, social, setting, categories],
  );

  const plan = async () => {
    if (!coords) {
      setStatus(gpsError || "No pude leer la ubicación. Permite ubicación exacta e inténtalo de nuevo.");
      return;
    }
    const gen = ++planGen.current;
    setBusy(true);
    setStops([]);
    setStatus(timeMode === "libre" ? "Calculando visitas y trayectos cortos…" : "Trazando ruta circular…");
    try {
      let pool = visiblePlaces;
      if (pool.length < 4) {
        const radii = cascadeRadiiMeters({ mode, minutes, stayLocal });
        const fetched = await fetchNearbyPlacesCascade({ ...coords, radii });
        if (gen !== planGen.current) return;
        setPlaces(fetched.places);
        pool = placesForPlan(filterPlacesByCategories(fetched.places, categories), { social, setting });
      }
      const route =
        timeMode === "libre"
          ? await buildLeisureRoute({
              origin: coords,
              places: pool,
              mode,
              totalMinutes: minutes,
              stayLocal,
              social,
              setting,
              minInterest: categories.length ? 1 : undefined,
              dwellFor: (place) => askDwellMinutes(place.name, place.kind),
            })
          : buildCircularRoute({
              origin: coords,
              places: pool,
              mode,
              totalMinutes: minutes,
              stayLocal,
              social,
              setting,
              minInterest: categories.length ? 1 : undefined,
            });
      if (gen !== planGen.current) return;
      setStops(route.stops);
      setStatus(
        `${timeMode === "libre" ? "Tiempo libre" : "Trayecto"} (~${route.totalMinutes} min): ${route.names}`,
      );
    } catch {
      if (gen === planGen.current) setStatus("No pude trazar la ruta ahora. Reintenta.");
    } finally {
      if (gen === planGen.current) setBusy(false);
    }
  };

  const mapHtml = coords
    ? leafletMapHtml(
        coords,
        coords,
        stops,
        visiblePlaces.slice(0, 30).map((place) => ({
          latitude: place.latitude,
          longitude: place.longitude,
          name: `${place.name} · ${PLACE_KIND_LABELS[place.kind] || place.kind}`,
        })),
      )
    : null;

  return (
    <div className="page-routes">
      <section className="routes-intro">
        <p className="eyebrow">RUTA</p>
        <h1 className="section-title">
          Mapa <span className="brand-inline">HERMES</span>
        </h1>
        <p className="lead">
          El GPS se actualiza en vivo. Los puntos verdes son sitios cercanos; al buscar ruta se eligen paradas coherentes.
        </p>
        {routeKind === "aleatoria" ? <p className="hero-meta">{status}</p> : null}
        <div className="time-row route-kind">
          <button type="button" className={`time-chip${routeKind === "aleatoria" ? " on" : ""}`} onClick={() => setRouteKind("aleatoria")}>
            🎲 Ruta aleatoria
          </button>
          <button type="button" className={`time-chip${routeKind === "semi" ? " on" : ""}`} onClick={() => setRouteKind("semi")}>
            🧭 Ruta semipersonalizada
          </button>
        </div>
      </section>

      {routeKind === "semi" ? (
        <SemiRoutePanel gps={coords ? { latitude: coords.latitude, longitude: coords.longitude } : null} />
      ) : (
      <>
      <section className="surprise-controls">
        <p className="label">Tipo de tiempo</p>
        <div className="time-row">
          <button
            type="button"
            className={`time-chip${timeMode === "trayecto" ? " on" : ""}`}
            onClick={() => setTimeMode("trayecto")}
          >
            Solo trayecto
          </button>
          <button
            type="button"
            className={`time-chip${timeMode === "libre" ? " on" : ""}`}
            onClick={() => setTimeMode("libre")}
          >
            Tiempo libre
          </button>
        </div>
        <p className="label">Tiempo total</p>
        <div className="time-row">
          {TIME_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              className={`time-chip${minutes === m ? " on" : ""}`}
              onClick={() => setMinutes(m)}
            >
              {m < 60 ? `${m} min` : `${m / 60}h`}
            </button>
          ))}
        </div>
        <p className="label">Cómo vas</p>
        <div className="time-row">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`time-chip${mode === m.key ? " on" : ""}`}
              onClick={() => setMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="label">Compañía</p>
        <div className="social-grid">
          {SOCIAL_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`social-chip${social === opt.key ? " on" : ""}`}
              onClick={() => setSocial(opt.key)}
            >
              <span aria-hidden>{opt.icon}</span>
              {opt.label}
            </button>
          ))}
        </div>
        <div className="toggle-row">
          <label>
            <input type="radio" name="route-setting" checked={setting === "any"} onChange={() => setSetting("any")} />
            Da igual
          </label>
          <label>
            <input
              type="radio"
              name="route-setting"
              checked={setting === "indoor"}
              onChange={() => setSetting("indoor")}
            />
            Interiores
          </label>
          <label>
            <input
              type="radio"
              name="route-setting"
              checked={setting === "outdoor"}
              onChange={() => setSetting("outdoor")}
            />
            Exteriores
          </label>
        </div>
        <CategoryFilter selected={categories} onChange={setCategories} />
        <label className="stay-local">
          <input type="checkbox" checked={stayLocal} onChange={(e) => setStayLocal(e.target.checked)} />
          Sin salir del pueblo
        </label>
        <button type="button" className="btn-copper" onClick={() => void plan()} disabled={busy || !coords}>
          {busy ? "Trazando…" : "Buscar ruta"}
        </button>
      </section>

      {visiblePlaces.length ? (
        <div className="place-strip" aria-label="Sitios cercanos">
          {visiblePlaces.slice(0, 14).map((place) => (
            <span key={place.id} className="place-pill">
              <strong>{place.name}</strong>
              <span>
                {PLACE_KIND_LABELS[place.kind] || place.kind}
                {place.distanceMeters != null ? ` · ${place.distanceMeters} m` : ""}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {stops.length ? (
        <ol className="surprise-stops">
          {stops.map((stop, index) => (
            <li key={`${stop.id}-${index}`}>
              <span className="stop-n">{index + 1}</span>
              <div>
                <h3>{stop.name}</h3>
                <p>
                  {PLACE_KIND_LABELS[stop.kind] || stop.kind}
                  {stop.address ? ` · ${stop.address}` : ""}
                  {stop.distanceMeters != null ? ` · a ${stop.distanceMeters} m` : ""}
                </p>
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      <div className="map-frame">
        {mapHtml ? (
          <iframe
            key={
              coords
                ? `${coords.latitude.toFixed(4)},${coords.longitude.toFixed(4)},${stops.length},${visiblePlaces.length}`
                : "gps"
            }
            title="Ruta Hermes"
            srcDoc={mapHtml}
            sandbox="allow-scripts allow-same-origin"
            allow="geolocation"
            loading="eager"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div className="map-waiting">
            <p>Esperando tu ubicación GPS…</p>
            <p className="muted">Si tarda, activa la ubicación exacta del navegador.</p>
          </div>
        )}
      </div>

      {stops.length && coords ? (
        <a className="btn-copper map-open" href={googleMapsLoop(coords, stops, mode)} target="_blank" rel="noreferrer">
          Abrir ruta circular en Google Maps
        </a>
      ) : null}
      </>
      )}
    </div>
  );
}
